"use client";

import { useEffect, useRef, useState } from "react";
import { X, ExternalLink } from "lucide-react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { parseYouTubeUrl, buildEmbedUrl } from "@/utils/youtube";
import { TUTORIAL_CONFIG } from "./tutorial-config";
import { dismissTutorialNudge } from "@/lib/actions/tutorial-nudge";
import { phCapture } from "@/utils/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const UPSIDE_YOUTUBE_CHANNEL = "https://www.youtube.com/@UpsideTools";

export function TutorialNudgeBanner({
  dismissedToolIds,
}: {
  dismissedToolIds: string[];
}) {
  const pathname = usePathname();
  const [dismissedSet, setDismissedSet] = useState(
    () => new Set(dismissedToolIds)
  );
  const [shouldRender, setShouldRender] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoTutorial, setVideoTutorial] = useState<
    (typeof TUTORIAL_CONFIG)[string] | undefined
  >(undefined);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const rafRef = useRef<number | undefined>(undefined);
  const hasShownRef = useRef(false);
  const isClosingRef = useRef(false);

  useEffect(() => {
    setDismissedSet(new Set(dismissedToolIds));
  }, [dismissedToolIds]);

  const tutorial = pathname ? TUTORIAL_CONFIG[pathname] : undefined;
  const isVisible =
    tutorial !== undefined && !dismissedSet.has(tutorial.toolId);

  useEffect(() => {
    if (!isVisible) {
      if (!isClosingRef.current) {
        setIsOpen(false);
        setShouldRender(false);
      }
      hasShownRef.current = false;
      return;
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
      isClosingRef.current = false;
    }

    setShouldRender(true);
    rafRef.current = requestAnimationFrame(() => {
      setIsOpen(true);
      if (!hasShownRef.current && tutorial) {
        hasShownRef.current = true;
        phCapture("tutorial_nudge_shown", {
          tool_id: tutorial.toolId,
          tool_label: tutorial.toolLabel,
        });
      }
    });

    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [isVisible, tutorial]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  function close(action: "cta_clicked" | "dismissed") {
    if (!tutorial) return;

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }

    isClosingRef.current = true;
    setIsOpen(false);
    setDismissedSet((prev) => new Set(prev).add(tutorial.toolId));

    dismissTutorialNudge(tutorial.toolId, action).catch(() => {});

    phCapture(
      action === "cta_clicked"
        ? "tutorial_nudge_cta_clicked"
        : "tutorial_nudge_dismissed",
      {
        tool_id: tutorial.toolId,
        tool_label: tutorial.toolLabel,
      }
    );

    closeTimeoutRef.current = setTimeout(() => {
      setShouldRender(false);
      isClosingRef.current = false;
    }, 200);
  }

  function handleCtaClick() {
    setVideoTutorial(tutorial);
    close("cta_clicked");
    setVideoOpen(true);
  }

  if (!tutorial && !videoOpen) return null;
  if (!shouldRender && !videoOpen) return null;

  const modalTutorial = videoTutorial ?? tutorial;
  const videoData = modalTutorial
    ? parseYouTubeUrl(modalTutorial.videoUrl)
    : { youtubeId: null, startSeconds: undefined };

  return (
    <>
      {tutorial && <section
        className={cn(
          "flex-none overflow-hidden",
          "transition-[max-height,opacity] duration-200 ease-out motion-reduce:transition-none",
          isOpen
            ? "max-h-11 sm:max-h-14 opacity-100"
            : "max-h-0 opacity-0 pointer-events-none"
        )}
        aria-label="Tutorial suggestion"
        aria-hidden={!isOpen}
      >
        <div className="relative border-b border-primary/10 bg-primary/[0.03]">
          <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-primary/60 via-primary/30 to-transparent" />

          <div className="min-h-11 pl-4 pr-2 flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/25">
                Tutorial
              </span>
              <p className="hidden text-[13px] min-w-0 truncate sm:block">
                <span className="font-medium text-white/90">
                  New to {tutorial.toolLabel}?
                </span>
                <span className="text-white/45">
                  {" "}
                  Watch a quick tutorial to get started.
                </span>
              </p>
            </div>

            <div className="ml-auto flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleCtaClick}
                className="text-[13px] whitespace-nowrap font-medium text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1"
              >
                Watch →
              </button>
              <div className="w-px h-4 bg-white/10" />
              <button type="button"
                onClick={() => close("dismissed")}
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
                aria-label="Dismiss tutorial suggestion"
              >
                <X className="size-3.5" />
              </button>
            </div>
          </div>
        </div>
      </section>}

      {modalTutorial && (
      <Dialog
        open={videoOpen}
        onOpenChange={(open) => {
          setVideoOpen(open);
          if (!open) setVideoTutorial(undefined);
        }}
      >
        <DialogContent className="max-w-3xl p-0" contentClassName="p-0 gap-0" aria-describedby={undefined}>
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-base font-medium text-foreground/90">
              {modalTutorial.toolLabel} Tutorial
            </DialogTitle>
          </DialogHeader>

          <div className="p-4 pt-3">
            {videoOpen && videoData.youtubeId && (
              <div className="relative aspect-video rounded-md overflow-hidden bg-black/50 border border-white/[0.06]">
                <iframe
                  src={buildEmbedUrl(videoData.youtubeId, videoData.startSeconds)}
                  title={`${modalTutorial.toolLabel} Tutorial`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            )}

            {!videoData.youtubeId && (
              <div className="aspect-video rounded-md bg-black/20 border border-white/[0.06] flex items-center justify-center">
                <span className="text-muted-foreground text-sm">
                  Video unavailable
                </span>
              </div>
            )}

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
              <a
                href={modalTutorial.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm",
                  "text-muted-foreground hover:text-foreground",
                  "transition-colors duration-150"
                )}
              >
                <ExternalLink className="size-3.5" />
                Watch on YouTube
              </a>

              <a
                href={UPSIDE_YOUTUBE_CHANNEL}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "text-xs text-muted-foreground/60 hover:text-muted-foreground",
                  "transition-colors duration-150"
                )}
              >
                More tutorials →
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      )}
    </>
  );
}
